import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Typography, Box, Button, Paper, Divider, CircularProgress, Alert, Grid, Card, CardContent, CardActions } from '@mui/material';
import { CheckCircle, Download, ShoppingBag, ArrowBack } from '@mui/icons-material';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../context/CartContext';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { clearCart } = useCart();
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [downloadLinks, setDownloadLinks] = useState([]);
  const [error, setError] = useState(null);
  
  // Get order reference from URL query params
  const queryParams = new URLSearchParams(location.search);
  const orderReference = queryParams.get('reference') || queryParams.get('ref');
  const transactionId = queryParams.get('transaction_id');
  
  // Clear cart on successful payment - only run once when component mounts
  useEffect(() => {
    clearCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once
  
  // Fetch order details from Firestore with priority, then sessionStorage as fallback
  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates after unmount
    
    const fetchOrderDetails = async () => {
      if (!orderReference) {
        if (isMounted) {
          setError('No order reference found');
          setLoading(false);
        }
        return;
      }
      
      try {
        if (isMounted) setLoading(true);
        
        // Check if we have a payment status in sessionStorage first
        let paymentStatusData = null;
        try {
          const storedPaymentStatus = sessionStorage.getItem(`payment_status_${orderReference}`);
          if (storedPaymentStatus) {
            paymentStatusData = JSON.parse(storedPaymentStatus);
            console.log('Found payment status in sessionStorage:', paymentStatusData);
          }
        } catch (storageErr) {
          console.warn('Error reading payment status from sessionStorage:', storageErr);
        }
        
        // If we have a Firestore ID from the payment status, use that directly
        let firestoreId = paymentStatusData?.firestoreId || null;
        
        // Try to find the order in Firestore
        const ordersRef = collection(db, 'orders');
        let orderDoc = null;
        
        // First try to find by Firestore ID if we have one
        if (firestoreId) {
          try {
            const docRef = doc(db, 'orders', firestoreId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              orderDoc = docSnap;
              console.log('Found order using firestoreId from payment status:', firestoreId);
            }
          } catch (e) {
            console.warn('Error fetching by firestoreId:', e);
          }
        }
        
        // If not found, try to find by the reference directly
        if (!orderDoc) {
          try {
            const docRef = doc(db, 'orders', orderReference);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              orderDoc = docSnap;
              console.log('Found order using reference as Firestore ID');
            }
          } catch (e) {
            console.log('Not a valid Firestore ID, searching by reference...');
          }
        }
        
        // If not found, try to find by localOrderId field
        if (!orderDoc) {
          try {
            const localIdQuery = query(
              ordersRef, 
              where('localOrderId', '==', orderReference)
            );
            
            const querySnapshot = await getDocs(localIdQuery);
            if (!querySnapshot.empty) {
              orderDoc = querySnapshot.docs[0];
              console.log('Found order using localOrderId query');
            }
          } catch (e) {
            console.log('Error searching by localOrderId:', e);
          }
        }
        
        // If we found the order in Firestore, use it
        if (orderDoc && (orderDoc.exists?.() || orderDoc.data)) {
          if (!isMounted) return;
          
          const orderData = orderDoc.data();
          console.log('Using order data from Firestore:', orderDoc.id);
          
          setOrderDetails({
            id: orderDoc.id,
            ...orderData
          });
          
          // Extract download links for digital products
          if (orderData.items && orderData.items.length > 0) {
            const digitalItems = orderData.items.filter(item => 
              item.isDigitalDownload && item.audioFileUrl
            );
            
            if (digitalItems.length > 0) {
              setDownloadLinks(digitalItems.map(item => ({
                id: item.id,
                title: item.title,
                artist: item.artist,
                url: item.audioFileUrl,
                image: item.imageUrl
              })));
            }
          }
        } else {
          if (!isMounted) return;
          
          // Check if we have order data in sessionStorage as fallback
          try {
            // Try with the reference directly
            let sessionOrder = sessionStorage.getItem(`order_${orderReference}`);
            
            // If not found and we have a firestoreId, try with that
            if (!sessionOrder && firestoreId) {
              sessionOrder = sessionStorage.getItem(`order_${firestoreId}`);
            }
            
            if (sessionOrder) {
              const orderData = JSON.parse(sessionOrder);
              console.log('Using order data from sessionStorage');
              setOrderDetails(orderData);
              
              // Extract download links for digital products
              if (orderData.items && orderData.items.length > 0) {
                const digitalItems = orderData.items.filter(item => 
                  item.isDigitalDownload && item.audioFileUrl
                );
                
                if (digitalItems.length > 0) {
                  setDownloadLinks(digitalItems.map(item => ({
                    id: item.id,
                    title: item.title,
                    artist: item.artist,
                    url: item.audioFileUrl,
                    image: item.imageUrl
                  })));
                }
              }
            } else {
              setError('Order not found. It may take a few minutes to process.');
            }
          } catch (storageErr) {
            console.error('Error retrieving from sessionStorage:', storageErr);
            setError('Order not found. It may take a few minutes to process.');
          }
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error fetching order:', err);
        setError('Failed to load order details. Please try again later.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    fetchOrderDetails();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [orderReference]); // Only re-run if orderReference changes
  
  const handleDownload = (url, title) => {
    // Create an anchor element and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.replace(/\s+/g, '_')}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleContinueShopping = () => {
    navigate('/marketplace');
  };
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading order details...
        </Typography>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            Payment Successful!
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Thank you for your purchase
          </Typography>
        </Box>
        
        {error ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : null}
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Order Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {orderDetails ? (
            <Box>
              <Typography variant="body1">
                <strong>Order ID:</strong> {orderDetails.id || orderReference}
              </Typography>
              <Typography variant="body1">
                <strong>Transaction ID:</strong> {transactionId || orderDetails.transactionId || 'Processing'}
              </Typography>
              <Typography variant="body1">
                <strong>Date:</strong> {new Date(orderDetails.timestamp || Date.now()).toLocaleString()}
              </Typography>
              <Typography variant="body1">
                <strong>Total Amount:</strong> {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'UGX' }).format(orderDetails.total)}
              </Typography>
              <Typography variant="body1">
                <strong>Payment Method:</strong> Mobile Money ({orderDetails.mobileProvider})
              </Typography>
            </Box>
          ) : (
            <Typography>
              Your order has been processed successfully. You will receive a confirmation shortly.
            </Typography>
          )}
        </Box>
        
        {downloadLinks.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Your Downloads
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" paragraph>
              Your purchased music is ready for download. Click the download button for each track.
            </Typography>
            
            <Grid container spacing={2}>
              {downloadLinks.map((item) => (
                <Grid item xs={12} sm={6} key={item.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {item.image && (
                          <Box 
                            component="img" 
                            src={item.image} 
                            alt={item.title}
                            sx={{ width: 60, height: 60, mr: 2, objectFit: 'cover' }}
                          />
                        )}
                        <Box>
                          <Typography variant="h6" component="div">
                            {item.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.artist}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                    <CardActions>
                      <Button 
                        fullWidth
                        variant="contained" 
                        color="primary" 
                        startIcon={<Download />}
                        onClick={() => handleDownload(item.url, item.title)}
                      >
                        Download MP3
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBack />}
            onClick={handleContinueShopping}
            sx={{ mr: 2 }}
          >
            Continue Shopping
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<ShoppingBag />}
            onClick={() => navigate('/account/orders')}
          >
            View All Orders
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentSuccess;
